/**
 * ContentCard Component
 * Standardized card design for all content types across the site
 * Usage: Blog posts, Projects, Portfolio items, Key Projects, etc.
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowRight, FiClock, FiTag } from 'react-icons/fi';

const ContentCard = ({ 
  to, 
  category = '', 
  title, 
  excerpt = '', 
  description = '',
  metadata = {},
  status = null,
  onTogglePublish = null,
  onToggleHighlight = null,
  index = 0,
  children = null
}) => {
  const {
    readTime,
    tags = [],
    date,
    company,
    year,
    technologies = []
  } = metadata;

  // Use excerpt if provided, otherwise use description (truncated)
  // Memoize to prevent recalculation on every render
  const displayText = useMemo(() => excerpt || description, [excerpt, description]);

  // Handle status toggle clicks
  const handleStatusClick = (e, toggleFn) => {
    e.preventDefault(); // Prevent card link navigation
    e.stopPropagation(); // Stop event bubbling
    if (toggleFn) {
      toggleFn();
    }
  };

  return (
    <Card
      to={to}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.15 }}
    >
      {category && <Category>{category}</Category>}
      
      <Title>{title}</Title>
      
      {displayText && <Excerpt>{displayText}</Excerpt>}

      {/* Metadata section - flexible for different content types */}
      {(readTime || tags.length > 0 || date || company || year || technologies.length > 0) && (
        <MetaSection>
          {readTime && (
            <>
              <MetaItem>
                <FiClock size={14} />
                <span>{readTime} min read</span>
              </MetaItem>
            </>
          )}
          
          {company && (
            <MetaItem>
              <span>{company}</span>
            </MetaItem>
          )}
          
          {year && (
            <MetaItem>
              <span>{year}</span>
            </MetaItem>
          )}
          
          {date && !year && (
            <MetaItem>
              <span>{new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</span>
            </MetaItem>
          )}

          {tags.length > 0 && (
            <>
              {(readTime || company || year || date) && <Separator>•</Separator>}
              <Tag>
                <FiTag size={12} />
                {tags[0]}
              </Tag>
            </>
          )}
          
          {technologies.length > 0 && (
            <>
              {(readTime || company || year || date || tags.length > 0) && <Separator>•</Separator>}
              <Tag>
                <FiTag size={12} />
                {technologies[0]}
              </Tag>
            </>
          )}
        </MetaSection>
      )}

      {/* Custom children for additional content */}
      {children}

      {/* Status indicators for admin mode */}
      {status && (
        <StatusBar>
          <StatusBadge 
            $published={status.published}
            as="button"
            type="button"
            onClick={(e) => handleStatusClick(e, onTogglePublish)}
            disabled={!onTogglePublish}
            title={status.published ? 'Published - Click to set as Draft' : 'Draft - Click to Publish'}
          >
            {status.published ? '👁️' : '🙈'}
          </StatusBadge>
          <StatusBadge 
            $highlighted={status.highlighted}
            as="button"
            type="button"
            onClick={(e) => handleStatusClick(e, onToggleHighlight)}
            disabled={!onToggleHighlight}
            title={status.highlighted ? 'Featured - Click to remove from featured' : 'Not Featured - Click to feature on homepage'}
          >
            {status.highlighted ? '⭐' : '⭕'}
          </StatusBadge>
        </StatusBar>
      )}

      <ReadMoreLink>
        {to?.includes('/blog/') ? 'Read full article' : 'View details'} <FiArrowRight />
      </ReadMoreLink>
    </Card>
  );
};

// Styled Components
const Card = styled(motion(Link))`
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--accent-gradient);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-primary);

    &::before {
      transform: scaleX(1);
    }
  }
`;

const Category = styled.div`
  display: inline-block;
  background: rgba(255, 107, 107, 0.1);
  color: var(--accent);
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
  align-self: flex-start;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  line-height: 1.3;
  font-weight: 700;
`;

const Excerpt = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex-grow: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaSection = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 500;
`;

const Separator = styled.span`
  opacity: 0.5;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: var(--bg-secondary);
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.85rem;
`;

const StatusBar = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  z-index: 10;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.2s ease;
  cursor: pointer;
  opacity: 0.9;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  
  &:hover:not(:disabled) {
    opacity: 1;
    transform: scale(1.15);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  &:active:not(:disabled) {
    transform: scale(1.05);
  }
`;

const ReadMoreLink = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--accent-primary);
  font-weight: 600;
  margin-top: auto;
  
  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

ContentCard.propTypes = {
  to: PropTypes.string.isRequired,
  category: PropTypes.string,
  title: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
  description: PropTypes.string,
  metadata: PropTypes.shape({
    readTime: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.string),
    date: PropTypes.string,
    company: PropTypes.string,
    year: PropTypes.string,
    technologies: PropTypes.arrayOf(PropTypes.string)
  }),
  status: PropTypes.shape({
    published: PropTypes.bool,
    featured: PropTypes.bool
  }),
  onTogglePublish: PropTypes.func,
  onToggleHighlight: PropTypes.func,
  index: PropTypes.number,
  children: PropTypes.node
};

// Memoize ContentCard to prevent unnecessary re-renders when parent re-renders
// Only re-render if props actually change
export default React.memo(ContentCard);
